import { useGroupStorage } from '../../../../../../AppClients';
import { groupIdPrefixForAlertService } from '../../../../../../backend/features/groups/shared';
import { allowUserSendMessages, requireAnySystemPermission } from '../../../../../../backend/features/permissions/shared';
import { SectionListItemButton } from '../../../../../../ui/components/SectionListItemButton';
import { showDeleteEntityDialog } from '../../../../../../ui/components/showDeleteEntityDialog';
import { useContactManagementTranslation, useUserState } from '../../../../../../ui/hooks';
import { ContactManagementConfigContext } from '../../../../../../ui/providers';
import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { ModalSlideout, ModalSlideoutContent, ModalSlideoutHeader, ModalSlideoutTitleArea, ModalSlideoutTopBar, ModalSlideoutTopBarDefaultActions, useModalSlideoutScroll, } from '@juvare/common-web-ui/ModalSlideout';
import { NavigationContext } from '@juvare/common-web-ui/NavigationContext';
import { PartialError } from '@juvare/common-web-ui/PartialError';
import { showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { Box, CircularProgress, List } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router';
import { useGroup } from '../../../hooks/useGroup';
import { useGroupSectionsNew } from '../../../hooks/useGroupSectionsNew';
import { mapAccountFilterReverse } from '../../../utils/mapAccountFilterReverse';
export const groupSlideoutButtonIds = {
    messageMembers: 'message-members',
    delete: 'delete',
};
export const GroupSlideout = ({ slideoutMenuItems, groupId, onClose }) => {
    const { data, isLoading, error } = useGroup(groupId);
    const { isScrolledToTop, handleScroll, handleResetScroll } = useModalSlideoutScroll();
    const handleClose = (changed) => {
        handleResetScroll();
        onClose(changed);
    };
    return (React.createElement(ModalSlideout, { open: !!groupId, onClose: handleClose, "data-test": 'group-slideout' },
        React.createElement(Box, { onScroll: handleScroll, sx: {
                overflow: 'auto',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: themeColors.surface.primary,
            }, "data-test": 'group-details' },
            data && !error && (React.createElement(SlideoutDetails, { isScrolledToTop: isScrolledToTop, group: data, slideoutMenuItems: slideoutMenuItems, onClose: handleClose })),
            isLoading && !data && React.createElement(CircularProgress, null),
            !data && error ? React.createElement(PartialError, { error: error }) : null)));
};
const SlideoutDetails = ({ slideoutMenuItems, group, isScrolledToTop, onClose }) => {
    var _a, _b;
    const { t } = useContactManagementTranslation();
    const { notificationConfig, accountsTableCustomization } = React.useContext(ContactManagementConfigContext);
    const navigationContext = React.useContext(NavigationContext);
    const navigate = useNavigate();
    const userState = useUserState();
    const storageClient = useGroupStorage();
    const { data, isLoading, error } = useGroupSectionsNew();
    const menuItems = (_a = slideoutMenuItems === null || slideoutMenuItems === void 0 ? void 0 : slideoutMenuItems(group)) !== null && _a !== void 0 ? _a : [];
    const resolvedMapAccountFilterReverse = (_b = accountsTableCustomization === null || accountsTableCustomization === void 0 ? void 0 : accountsTableCustomization.overrideMapAccountFilterReverse) !== null && _b !== void 0 ? _b : mapAccountFilterReverse;
    if ((notificationConfig === null || notificationConfig === void 0 ? void 0 : notificationConfig.onGroupNotificationSend) && allowUserSendMessages(userState)) {
        menuItems.push({
            label: t('panels.groups.messageMembers') + ' ',
            id: groupSlideoutButtonIds.messageMembers,
            onClick: () => { var _a; return (_a = notificationConfig === null || notificationConfig === void 0 ? void 0 : notificationConfig.onGroupNotificationSend) === null || _a === void 0 ? void 0 : _a.call(notificationConfig, groupIdPrefixForAlertService + group.id); },
        });
    }
    menuItems.push({
        label: t('common.delete'),
        id: groupSlideoutButtonIds.delete,
        onClick: () => {
            showDeleteEntityDialog({
                entity: t('panels.groups.entity'),
                t: t,
                onConfirm: () => {
                    storageClient
                        .post({
                        action: 'delete',
                        id: group.id,
                    })
                        .then(() => {
                        showInfoSnackbar({
                            message: t('common.itemDeleted', { item: group.data.name }),
                            autoHideDuration: 2000,
                        });
                        onClose(true);
                    })
                        .catch(handleErrorDialog);
                },
            });
        },
    });
    const sections = data === null || data === void 0 ? void 0 : data.rows;
    return (React.createElement(React.Fragment, null,
        sections && !error && (React.createElement(React.Fragment, null,
            React.createElement(ModalSlideoutTopBar, { isOpaque: !isScrolledToTop },
                React.createElement(ModalSlideoutTopBarDefaultActions, { menuItems: menuItems, onClose: onClose })),
            React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', flex: 1, gap: 0 } },
                React.createElement(ModalSlideoutHeader, { "data-test": 'header' },
                    React.createElement(ModalSlideoutTitleArea, { titleTypographyVariant: 'h5', title: group === null || group === void 0 ? void 0 : group.data.name, subTitle: group === null || group === void 0 ? void 0 : group.data.description, "data-test": 'group-name' })),
                React.createElement(ModalSlideoutContent, { ariaDescribeAllChildren: false, "data-test": 'group-section-list' },
                    React.createElement(List, { "data-test": 'group-section-list' },
                        sections.length === 0
                            ? null
                            : sections.map((section, ind) => (React.createElement(SectionListItemButton, { showTopDivider: ind === 0, key: section.id, primaryText: section.columns.title, secondaryText: section.columns.description, "data-test": section.columns.key ? 'section-' + section.columns.key : undefined, "data-test-section-name": section.columns.title, onClick: () => {
                                    navigationContext.navigate(() => navigate({
                                        pathname: group.id + '/' + section.columns.name.toLowerCase(),
                                        search: getUrlSearchParams(group.data.filterBased, group.data.criteria.filters, resolvedMapAccountFilterReverse, section.columns.key).toString(),
                                    }, { state: { back: true } }));
                                } }))),
                        requireAnySystemPermission(userState, 'view-group-history') && (React.createElement(React.Fragment, null, sections.length === 0 ? null : (React.createElement(SectionListItemButton, { showTopDivider: false, primaryText: t('panels.groups.history'), "data-test-section-name": 'history', onClick: () => navigationContext.navigate(() => navigate(group.id + '/history', { state: { back: true } })) }))))))))),
        isLoading && !sections && React.createElement(CircularProgress, null),
        !sections && !!error && React.createElement(PartialError, { error: error })));
};
function getUrlSearchParams(filterBased, filters, resolvedMapAccountFilterReverse, sectionKey) {
    const urlParams = new URLSearchParams();
    if (sectionKey === 'members') {
        let filter = {};
        if (filterBased) {
            filter = resolvedMapAccountFilterReverse(filters);
        }
        else {
            filter.systemStatus = ['active'];
        }
        urlParams.set('filter', JSON.stringify(filter));
    }
    return urlParams;
}
